import { Descriptions, Grid, Tabs } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as permissionKind from '../../../../../shared/permissions';
// eslint-disable-next-line max-len
import { type EventApplicationModuleWithPermissionsSpecsFragment } from '../../../../api/fragments/EventApplicationModuleWithPermissionsSpecs';
import { ContentRefinementSourceAction, ContentRefinementSourceKind } from '../../../../api/types';
import { GenericStateOnPageContextProvider } from '../../../../components/contexts/GenericStateOnPageContextManager';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import EventApplicationModuleAction from './EventApplicationModuleAction';
import EventApplicationModuleDetailsInner from './EventApplicationModuleDetailsInner';
import EventApplicationModuleFooter from './EventApplicationModuleFooter';
import { EditMode, HeaderTabs } from './type';

export type EventApplicationModulePageProps = { module: EventApplicationModuleWithPermissionsSpecsFragment };

const EventApplicationModulePage = ({ module }: EventApplicationModulePageProps) => {
    const { t } = useTranslation(['eventApplicationModuleDetails']);
    const [activeTab, setActiveTab] = useState(HeaderTabs.ModuleDetails);
    const [editMode, setEditMode] = useState(EditMode.ModuleDetails);
    const navigate = useNavigate();

    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: module.versioning,
        timeZone: module.company.timeZone,
    });

    const screens = Grid.useBreakpoint();

    const permissions = {
        hasUpdatePermission: hasPermissions(module.permissions, [permissionKind.updateModule]),
        hasDeletePermission: hasPermissions(module.permissions, [permissionKind.deleteModule]),
    };

    const refineContentParams = useMemo(
        () => ({
            hasPermissionToRefineContent: permissions.hasUpdatePermission,
            source: {
                kind: ContentRefinementSourceKind.Module,
                action: ContentRefinementSourceAction.Update,
                identifier: module.id,
            },
        }),
        [module.id, permissions.hasUpdatePermission]
    );

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('eventApplicationModuleDetails:content.displayName')}>
                {module.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('eventApplicationModuleDetails:content.company')}>
                {module.company.displayName}
            </Descriptions.Item>

            <Descriptions.Item label={t('eventApplicationModuleDetails:content.agreement')}>
                {module.agreementsModule.displayName}
            </Descriptions.Item>

            <Descriptions.Item label={t('eventApplicationModuleDetails:content.customer')}>
                {`${module.customerModule.displayName} - ${module.kycPresets
                    .map(kycPreset => kycPreset.displayName)
                    .join(', ')}`}
            </Descriptions.Item>

            <Descriptions.Item label={t('eventApplicationModuleDetails:content.vehicle')}>
                {module.vehicleModule.displayName}
            </Descriptions.Item>

            {module.appointmentModule ? (
                <Descriptions.Item label={t('eventApplicationModuleDetails:content.appointment')}>
                    {module.appointmentModule.displayName}
                </Descriptions.Item>
            ) : null}
            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    useEffect(() => {
        switch (editMode) {
            case EditMode.Vehicles:
                setActiveTab(HeaderTabs.Vehicles);
                break;

            default:
                break;
        }
    }, [editMode]);

    return (
        <GenericStateOnPageContextProvider refineContent={refineContentParams}>
            <ConsolePageWithHeader
                content={content}
                extra={
                    <EventApplicationModuleAction module={module} permissions={permissions} setEditMode={setEditMode} />
                }
                footer={[<EventApplicationModuleFooter editMode={editMode} setEditMode={setEditMode} />]}
                header={{
                    footer: (
                        <Tabs
                            activeKey={activeTab}
                            onChange={(activeKey: HeaderTabs) => {
                                switch (activeKey) {
                                    case 'moduleDetails':
                                        setEditMode(EditMode.ModuleDetails);
                                        break;

                                    default:
                                        setEditMode(null);
                                }
                                setActiveTab(activeKey);
                            }}
                        >
                            <Tabs.TabPane
                                key={HeaderTabs.ModuleDetails}
                                tab={t('eventApplicationModuleDetails:tabs.moduleDetails.title')}
                            />
                            <Tabs.TabPane
                                key={HeaderTabs.Vehicles}
                                tab={t('eventApplicationModuleDetails:tabs.vehicles.title')}
                            />

                            <Tabs.TabPane
                                key={HeaderTabs.EmailContents}
                                tab={t('eventApplicationModuleDetails:tabs.emailContents.title')}
                            />
                        </Tabs>
                    ),
                }}
                onBack={() => navigate('/admin/system/modules')}
                title={
                    screens.md
                        ? t('eventApplicationModuleDetails:title', { name: module.displayName })
                        : module.displayName
                }
            >
                <EventApplicationModuleDetailsInner
                    activeTab={activeTab}
                    editMode={editMode}
                    module={module}
                    setEditMode={setEditMode}
                />
            </ConsolePageWithHeader>
        </GenericStateOnPageContextProvider>
    );
};

export default EventApplicationModulePage;
